import type { NextRequest } from 'next/server'

import { type ClassValue, clsx } from 'clsx'
import { addWeeks, endOfWeek, startOfWeek, subWeeks } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { twMerge } from 'tailwind-merge'

interface WeekRange {
  startDate: Date
  endDate: Date
}

export const TOKEN_NAME_COOKIE = 'next-auth.access-token'
export const TENANT_ID_COOKIE = 'next-auth.tenant-id'
export const TENANT_DATA_COOKIE = 'next-auth.tenant-data'
export const SETTINGS_DATA_COOKIE = 'next-auth.settings-data'
export const MAX_AGE_COOKIE = 525600

export function getDomain(req: NextRequest) {
  const url = req.nextUrl.clone()

  return url.hostname
}

export function getSubdomain(host?: string | null) {
  let subdomain: string | null = null

  if (!host && typeof window !== 'undefined') {
    // On client side, get the host from window
    host = window.location.host
  }
  if (host && host.includes('.')) {
    const candidate = host.split('.')[0]
    if (candidate && !candidate.includes('localhost')) {
      // Valid candidate
      subdomain = candidate
    }
  }

  return subdomain
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const formatCurrency = (amount: number) => {
  return (amount / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  })
}

export const formatDateToLocal = (
  dateStr: string,
  locale: string = 'en-US',
) => {
  const date = new Date(dateStr)
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }
  const formatter = new Intl.DateTimeFormat(locale, options)

  return formatter.format(date)
}

export function convertTime(time: number): string {
  if (time === 0) return '00:00'

  const hours = Math.floor(time / 3600)
    .toString()
    .padStart(2, '0')
  const minutes = Math.floor((time / 60) % 60)
    .toString()
    .padStart(2, '0')
  const seconds = Math.floor(time % 60)
    .toString()
    .padStart(2, '0')

  return [hours, minutes, seconds].join(':')
}

export function getWeekRange(weeksToAdd: number = 0): WeekRange {
  const currentDate = new Date()

  const startDate =
    weeksToAdd >= 0
      ? startOfWeek(addWeeks(currentDate, weeksToAdd), {
          weekStartsOn: 1,
          locale: ptBR,
        })
      : startOfWeek(subWeeks(currentDate, Math.abs(weeksToAdd)), {
          weekStartsOn: 1,
          locale: ptBR,
        })

  const endDate =
    weeksToAdd >= 0
      ? endOfWeek(addWeeks(currentDate, weeksToAdd), {
          weekStartsOn: 1,
          locale: ptBR,
        })
      : endOfWeek(subWeeks(currentDate, Math.abs(weeksToAdd)), {
          weekStartsOn: 1,
          locale: ptBR,
        })

  return {
    startDate,
    endDate,
  }
}

export const getCurrentDate = (): string => {
  const date = new Date()
  return date.toISOString().split('T')[0]
}

export const timeStringToSeconds = (timeString: string): number => {
  const [minutes, seconds] = timeString.split(':').map(Number)

  return minutes * 60 + (seconds || 0)
}

export const convertTimeToMinutes = (time: string) => {
  const [hours, minutes, seconds] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60 + (seconds || 0)
}

export function formatHours(hours: number) {
  if (typeof hours !== 'number' || hours < 0) {
    throw new Error('O valor deve ser um número inteiro não negativo.')
  }

  // Garante que o valor está no formato `HH:mm`
  return `${String(Math.floor(hours)).padStart(2, '0')}:00`
}

/**
 * Converte segundos para o formato HH:mm
 */
export const convertSecondsToTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  return `${String(hours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}`
}

/**
 * Converte o formato HH:mm para segundos
 */
export const convertTimeToSeconds = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 3600 + minutes * 60
}
